import type { AuthProviders } from "./sessionAuth";

/** Browser-side state for one "Sign in with Google" attempt, kept only until the callback reads it. */
export const OAUTH_ATTEMPT_KEY = "gar.oauth.attempt";

export type OAuthAttempt = {
  state: string;
  verifier: string;
  invite: string | null;
  /** Optional workspace name collected in-app before Google (signup); applied after exchange. */
  workspaceName: string | null;
};

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function randomUrlSafe(byteLength: number): string {
  return base64UrlEncode(crypto.getRandomValues(new Uint8Array(byteLength)));
}

export async function s256Challenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}

/** Must match an entry in the backend allowlist (default: {publicAppUrl}/callback). */
export function oauthRedirectUri(): string {
  return `${window.location.origin}/callback`;
}

export type StartGoogleLoginOptions = {
  invite?: string | null;
  workspaceName?: string | null;
};

/**
 * Sends the browser to Keycloak authorize with kc_idp_hint=google so Google opens directly.
 * Never uses Keycloak registration — new users are created by the broker, extra fields in our UI.
 */
export async function startGoogleLogin(
  providers: AuthProviders,
  inviteOrOptions?: string | null | StartGoogleLoginOptions
): Promise<void> {
  if (!providers.googleEnabled || !providers.keycloakAuthorizeUrl || !providers.clientId || !providers.googleIdpHint) {
    throw new Error("Google sign-in is not available");
  }
  const options: StartGoogleLoginOptions =
    inviteOrOptions != null && typeof inviteOrOptions === "object"
      ? inviteOrOptions
      : { invite: inviteOrOptions ?? null };

  const state = randomUrlSafe(24);
  const verifier = randomUrlSafe(64);
  const challenge = await s256Challenge(verifier);
  const attempt: OAuthAttempt = {
    state,
    verifier,
    invite: options.invite || null,
    workspaceName: options.workspaceName?.trim() || null,
  };
  sessionStorage.setItem(OAUTH_ATTEMPT_KEY, JSON.stringify(attempt));

  const url = new URL(providers.keycloakAuthorizeUrl);
  // prompt=login avoids a sticky Keycloak session that might show login/register UI instead of Google.
  url.search = new URLSearchParams({
    client_id: providers.clientId,
    response_type: "code",
    scope: "openid profile email",
    redirect_uri: oauthRedirectUri(),
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
    kc_idp_hint: providers.googleIdpHint,
    prompt: "login",
  }).toString();
  window.location.assign(url.toString());
}

/** Reads and clears the pending attempt; single use, so a replayed callback finds nothing. */
export function takeOAuthAttempt(): OAuthAttempt | null {
  try {
    const raw = sessionStorage.getItem(OAUTH_ATTEMPT_KEY);
    sessionStorage.removeItem(OAUTH_ATTEMPT_KEY);
    return raw ? (JSON.parse(raw) as OAuthAttempt) : null;
  } catch {
    return null;
  }
}
